// src/components/dash/LeaveDashboard.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import StatusCard from '../cards/StatusCard';
import EmployeeAbsenceTable from '../tables/EmployeeAttendance';
import DashboardFilters from '../controls/OfficeMonthYear';

// APIs
import {
  getLeaveCountByTypeFiltered,
  getLatestLeavesFiltered,
  getLeavesByDepartmentFiltered,
  getLeaveSummaryByEmployeeAndTypeFiltered,
  getLeaveRequestsCountByStatusFiltered,
} from '../../services/leavesAPIs';

const LeaveDashboard = ({ onStatusClick, filters, setFilters }) => {
  const theme = useTheme();

  const [loading, setLoading] = useState(true);
  const [leaveSummary, setLeaveSummary] = useState({});
  const [latestLeaves, setLatestLeaves] = useState([]);
  const [leavesByDepartment, setLeavesByDepartment] = useState({});
  const [employees, setEmployees] = useState([]);

  // Destructure shared filters
  const { periodType, periodValue, office } = filters;

  // Handlers for filter changes - updated to accept values directly
  const handlePeriodTypeChange = (newType) => {
    setFilters((prev) => ({
      ...prev,
      periodType: newType,
      periodValue: newType === 'year' ? '2025' : '2025-06', // default values
    }));
  };

  const handlePeriodValueChange = (newValue) => {
    setFilters((prev) => ({ ...prev, periodValue: newValue }));
  };

  const handleOfficeChange = (newOffice) => {
    setFilters((prev) => ({ ...prev, office: newOffice }));
  };

  // Generate options based on periodType
  const getPeriodOptions = () => {
    if (periodType === 'year') {
      return [
        { value: '2024', label: '2024' },
        { value: '2025', label: '2025' },
        { value: '2026', label: '2026' },
      ];
    }
    if (periodType === 'month') {
      return Array.from({ length: 12 }, (_, i) => {
        const date = new Date(2025, i);
        return {
          value: `2025-${String(i + 1).padStart(2, '0')}`,
          label: date.toLocaleString('default', { month: 'long' }),
        };
      });
    }
    return [];
  };

  // Extract year and month from filters
  const year = periodType === 'year' ? periodValue : periodValue?.split('-')[0] || null;
  const month = periodType === 'month' ? periodValue?.split('-')[1] || null : null;

  // State for status counts
  const [statusCounts, setStatusCounts] = useState({
    Pending: 0,
    Approved: 0,
    Rejected: 0,
    Cancelled: 0,
  });

  // Load filtered data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [
          summary,
          latest,
          byDept,
          empSummary,
          pendingCount,
          approvedCount,
          rejectedCount,
          cancelledCount,
        ] = await Promise.all([
          getLeaveCountByTypeFiltered(year, month, office),
          getLatestLeavesFiltered(year, month, office),
          getLeavesByDepartmentFiltered(year, month, office),
          getLeaveSummaryByEmployeeAndTypeFiltered(year, month, office),

          // Status counts
          getLeaveRequestsCountByStatusFiltered('pending', year, month, office),
          getLeaveRequestsCountByStatusFiltered('approved', year, month, office),
          getLeaveRequestsCountByStatusFiltered('rejected', year, month, office),
          getLeaveRequestsCountByStatusFiltered('cancelled', year, month, office),
        ]);

        // Transform employee summary
        const transformedEmployees = Object.entries(empSummary).map(([name, types]) => {
          const [firstName, lastName] = name.split(' ');
          return { firstName, lastName, ...types };
        });

        setLeaveSummary(summary);
        setLatestLeaves(latest);
        setLeavesByDepartment(byDept);
        setEmployees(transformedEmployees);

        // Update status counts
        setStatusCounts({
          Pending: pendingCount || 0,
          Approved: approvedCount || 0,
          Rejected: rejectedCount || 0,
          Cancelled: cancelledCount || 0,
        });
      } catch (error) {
        console.error('Error loading leave dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [periodType, periodValue, office]); // Only re-run when filters change

  // Helpers
  const getLeaveTypeColor = (type) => {
    const colors = {
      SICK_LEAVE: '#f39c12',
      ANNUAL_LEAVE: '#4ecdc4',
      UNPAID_LEAVE: '#95a5a6',
      MATERNITY_LEAVE: '#f9ca24',
      PATERNITY_LEAVE: '#9b59b6',
      EMERGENCY_LEAVE: '#e74c3c',
    };
    return colors[type] || '#6c757d';
  };

  const getLeaveTypeIcon = (type) => {
    const icons = {
      SICK_LEAVE: '🏥',
      ANNUAL_LEAVE: '🏖️',
      UNPAID_LEAVE: '💸',
      MATERNITY_LEAVE: '👶',
      PATERNITY_LEAVE: '👨‍👦',
      EMERGENCY_LEAVE: '🚨',
    };
    return icons[type] || '📅';
  };

  const statusConfig = {
    Pending: { icon: '⏳', color: '#ffa726' },
    Approved: { icon: '✅', color: '#66bb6a' },
    Rejected: { icon: '❌', color: '#ef5350' },
    Cancelled: { icon: '🚫', color: '#78909c' },
  };

  const formatLeaveType = (type) =>
    type
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress size={60} thickness={4} sx={{ color: '#3d52a0' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ fontFamily: 'Roboto, sans-serif', minWidth: '100%' }}>
      {/* Filters */}
      <DashboardFilters
        filtersOpen={true}
        setFiltersOpen={() => {}}
        periodType={periodType}
        setPeriodType={handlePeriodTypeChange}
        periodValue={periodValue}
        setPeriodValue={handlePeriodValueChange}
        office={office}
        setOffice={handleOfficeChange}
        getPeriodOptions={getPeriodOptions}
      />

      {/* Status Cards */}
      <Box sx={{ width: '100%', mt: 2 }}>
        <Typography variant="h6" sx={{ color: '#3d52a0', mb: 2, fontWeight: 'bold' }}>
          📮 Leave Request Status
        </Typography>
        <Grid container spacing={2}>
          {Object.entries(statusCounts).map(([status, count]) => (
            <Grid item xs={6} md={3} key={status} sx={{ width: '23.6%' }}>
              <StatusCard
                title={status}
                count={count}
                icon={statusConfig[status].icon}
                color={statusConfig[status].color}
                onClick={() => onStatusClick?.(status)}
              />
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Leave Summary by Type */}
      <Box sx={{ width: '100%', mt: 2 }}>
        <Typography variant="h6" sx={{ color: '#3d52a0', mb: 2, fontWeight: 'bold' }}>
          📊 Leave Summary by Type
        </Typography>
        <EmployeeAbsenceTable
          employees={employees}
          getLeaveTypeColor={getLeaveTypeColor}
          getLeaveTypeIcon={getLeaveTypeIcon}
          formatLeaveType={formatLeaveType}
          leave={true}
        />
      </Box>
    </Box>
  );
};

export default LeaveDashboard;

// src/components/dash/LeaveDashboard.jsx
// import React, { useState, useEffect } from 'react';
// import {
//   Box,
//   Typography,
//   Grid,
//   CircularProgress,
//   useMediaQuery,
// } from '@mui/material';
// import { SentimentSatisfied } from '@mui/icons-material';
// import { useTheme } from '@mui/material/styles';

// import StatusCard from '../cards/StatusCard';
// import EmpStatCard from '../cards/EmpStatCard';
// import EmployeeAbsenceTable from '../tables/EmployeeAttendance';
// import DepartmentCard from '../cards/DepartmentCard';
// import LatestAbsences from '../lists/LatestAbsences';
// import DashboardFilters from '../controls/OfficeMonthYear';

// // New filtered APIs (to be created)
// import {
//   getLeaveCountByTypeFiltered,
//   getLatestLeavesFiltered,
//   getLeavesByDepartmentFiltered,
//   getLeaveSummaryByEmployeeAndTypeFiltered,
//   getLeaveRequestsCountByStatusFiltered 
// } from '../../services/leavesAPIs';

// const LeaveDashboard = ({ onStatusClick }) => {
//   const theme = useTheme();
//   const isLargeScreen = useMediaQuery('(min-width: 1600px)');

//   const [loading, setLoading] = useState(true);
//   const [leaveSummary, setLeaveSummary] = useState({});
//   const [latestLeaves, setLatestLeaves] = useState([]);
//   const [leavesByDepartment, setLeavesByDepartment] = useState({});
//   const [employees, setEmployees] = useState([]);

//   // Filters
//   const [filtersOpen, setFiltersOpen] = useState(true);
//   const [periodType, setPeriodType] = useState('year');
//   const [periodValue, setPeriodValue] = useState(new Date().getFullYear().toString());
//   const [office, setOffice] = useState('All');

//   // Status
//   const [statusCounts, setStatusCounts] = useState({
//     Pending: 0,
//     Approved: 0,
//     Rejected: 0,
//     Cancelled: 0,
//   });

//   const getPeriodOptions = () => {
//     if (periodType === 'year') {
//       return [
//         { value: '2024', label: '2024' },
//         { value: '2025', label: '2025' },
//         { value: '2026', label: '2026' },
//       ];
//     }
//     if (periodType === 'month') {
//       return Array.from({ length: 12 }, (_, i) => {
//         const date = new Date(2025, i);
//         return {
//           value: `2025-${String(i + 1).padStart(2, '0')}`,
//           label: date.toLocaleString('default', { month: 'long' }),
//         };
//       });
//     }
//     return [];
//   };

//   // Load filtered data
//   useEffect(() => {
//     const loadData = async () => {
//       setLoading(true);
//       setLeaveSummary({});
//       setLatestLeaves([]);
//       setLeavesByDepartment({});
//       setEmployees([]);

//       try {
//         const year = periodType === 'year' ? periodValue : periodValue.split('-')[0];
//         const month = periodType === 'month' ? periodValue.split('-')[1] : null;

//         const [summary, latest, byDept, empSummary, pendingCount, approvedCount, rejectedCount, cancelledCount] = await Promise.all([
//           getLeaveCountByTypeFiltered(year, month, office),
//           getLatestLeavesFiltered(year, month, office),
//           getLeavesByDepartmentFiltered(year, month, office),
//           getLeaveSummaryByEmployeeAndTypeFiltered(year, month, office),

//           // Status calls
//           getLeaveRequestsCountByStatusFiltered('pending', year, month, office),
//           getLeaveRequestsCountByStatusFiltered('approved', year, month, office),
//           getLeaveRequestsCountByStatusFiltered('rejected', year, month, office),
//           getLeaveRequestsCountByStatusFiltered('cancelled', year, month, office)
//         ]);
//         console.log('Employee Summary:', empSummary);

//         // Transform employee summary
//         const transformedEmployees = Object.entries(empSummary).map(([name, types]) => {
//           const [firstName, lastName] = name.split(' ');
//           return { firstName, lastName, ...types };
//         });
//         console.log('Transformed Employees:', transformedEmployees);

//         setLeaveSummary(summary);
//         setLatestLeaves(latest);
//         setLeavesByDepartment(byDept);
//         setEmployees(transformedEmployees);

//         // Status update
//         setStatusCounts({
//           Pending: pendingCount || 0,
//           Approved: approvedCount || 0,
//           Rejected: rejectedCount || 0,
//           Cancelled: cancelledCount || 0,
//         });
//       } catch (error) {
//         console.error('Error loading leave dashboard data:', error);
//       } finally {
//         setLoading(false);
//       }
//     };

//     loadData();
//   }, [periodType, periodValue, office]);

//   // Helpers
//   const getTotalDays = () => {
//     return Object.values(leaveSummary).reduce((sum, item) => sum + item.days, 0);
//   };

//   const filterEmployeesByType = (type) => {
//     return employees.filter(emp => emp.leaveType === type);
//   };

//   const getLeaveTypeColor = (type) => {
//     const colors = {
//       SICK_LEAVE: '#f39c12',
//       ANNUAL_LEAVE: '#4ecdc4',
//       UNPAID_LEAVE: '#95a5a6',
//       MATERNITY_LEAVE: '#f9ca24',
//       PATERNITY_LEAVE: '#9b59b6',
//       EMERGENCY_LEAVE: '#e74c3c',
//     };
//     return colors[type] || '#6c757d';
//   };

//   const getLeaveTypeIcon = (type) => {
//     const icons = {
//       SICK_LEAVE: '🏥',
//       ANNUAL_LEAVE: '🏖️',
//       UNPAID_LEAVE: '💸',
//       MATERNITY_LEAVE: '👶',
//       PATERNITY_LEAVE: '👨‍👦',
//       EMERGENCY_LEAVE: '🚨',
//     };
//     return icons[type] || '📅';
//   };
//   // Status config: icon and color
//   const statusConfig = {
//     Pending: { icon: '⏳', color: '#ffa726' },     // Orange
//     Approved: { icon: '✅', color: '#66bb6a' },   // Green
//     Rejected: { icon: '❌', color: '#ef5350' },   // Red
//     Cancelled: { icon: '🚫', color: '#78909c' },  // Gray-blue
//   };


//   const formatLeaveType = (type) => {
//     return type
//       .split('_')
//       .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
//       .join(' ');
//   };

//   const getDepartmentStats = (dept, leaves) => {
//     const totalDays = leaves.reduce((sum, leave) => sum + leave.days, 0);
//     const totalEmployees = new Set(leaves.map(l => `${l.employee.firstName} ${l.employee.lastName}`)).size;
//     return { totalDays, totalEmployees };
//   };

//   if (loading) {
//     return (
//       <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
//         <CircularProgress size={60} thickness={4} sx={{ color: '#3d52a0' }} />
//       </Box>
//     );
//   }

//   return (
//     <Box sx={{ fontFamily: 'Roboto, sans-serif', minWidth: '100%' }}>
//       {/* Filters */}
//       <DashboardFilters
//         filtersOpen={filtersOpen}
//         setFiltersOpen={setFiltersOpen}
//         periodType={periodType}
//         setPeriodType={setPeriodType}
//         periodValue={periodValue}
//         setPeriodValue={setPeriodValue}
//         office={office}
//         setOffice={setOffice}
//         getPeriodOptions={getPeriodOptions}
//       />
//       {/* Status Cards */}
//         <Box sx={{ width: '100%', mt: 2, }}>
//           <Typography variant="h6" sx={{ color: '#3d52a0', mb: 2, fontWeight: 'bold' }}>
//             📮 Leave Request Status
//           </Typography>
//           <Grid container spacing={2}>
//             {Object.entries(statusCounts).map(([status, count]) => (
//               <Grid item xs={6} md={3} key={status} sx={{ width:'23.6%' }}>
//                 <StatusCard
//                   title={status}
//                   count={count}
//                   icon={statusConfig[status].icon}
//                   color={statusConfig[status].color}
//                   onClick={() => onStatusClick?.(status)}
//                />
//               </Grid>
//             ))}
//           </Grid>
//         </Box>

//       {/* Leave Summary by Type */}
//       <Box sx={{ width: '100%', mt: 2 }}>
//         <Typography variant="h6" sx={{ color: '#3d52a0', mb: 2, fontWeight: 'bold' }}>
//           📊 Leave Summary by Type
//         </Typography>
//           <EmployeeAbsenceTable
//             employees={employees}
//             getLeaveTypeColor={getLeaveTypeColor}
//             getLeaveTypeIcon={getLeaveTypeIcon}
//             formatLeaveType={formatLeaveType}
//             leave={true}
//           />
//         {/* {isLargeScreen ? (
//           <Box sx={{ display: 'flex', gap: 3, flexDirection: 'row', width: '99.8%' }}>
//             {Object.entries(leaveSummary).map(([type, { days, count }]) => (
//               <Box key={type} sx={{ flex: 1 }}>
//                 <EmpStatCard
//                   type={type}
//                   days={days}
//                   count={count}
//                   totalDays={getTotalDays()}
//                   getLeaveTypeColor={getLeaveTypeColor}
//                   getLeaveTypeIcon={getLeaveTypeIcon}
//                   formatLeaveType={formatLeaveType}
//                   employees={filterEmployeesByType(type)}
//                 />
//               </Box>
//             ))}
//           </Box>
//         ) : (
//           <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, width: '100%' }}>
//             {Object.entries(leaveSummary).map(([type, { days, count }]) => (
//                 console.log('Rendering type:', type, days, count, filterEmployeesByType(type)),
//               <Box key={type}>
               
//                 <EmpStatCard
//                   type={type}
//                   days={days}
//                   count={count}
//                   totalDays={getTotalDays()}
//                   getLeaveTypeColor={getLeaveTypeColor}
//                   getLeaveTypeIcon={getLeaveTypeIcon}
//                   formatLeaveType={formatLeaveType}
//                   employees={filterEmployeesByType(type)}
//                 />
//               </Box>
//             ))}
//           </Box>
//         )} */}
//       </Box>

//       {/* Department & Latest Leaves */}
//       <Box sx={{ display: 'flex', gap: 2, width: '100%', mt: 1, flexWrap: 'wrap' }}>
//         {/* Leaves by Department */}
//         {/* <Box sx={{ mt: 4, width: isLargeScreen ? '65%' : '65%' }}>
//           <Typography variant="h6" sx={{ color: '#3d52a0', mb: 2, fontWeight: 'bold' }}>
//             🏢 Leaves by Department
//           </Typography>
//           {Object.keys(leavesByDepartment).length === 0 ? (
//             <Box
//               sx={{
//                 background: 'linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%)',
//                 display: 'flex',
//                 flexDirection: 'column',
//                 alignItems: 'center',
//                 justifyContent: 'center',
//                 py: 4,
//                 textAlign: 'center',
//                 width: '100%',
//                 boxShadow: theme.shadows[6],
//                 borderRadius: '16px',
//                 border: '1px solid rgba(103, 58, 183, 0.2)',
//               }}
//             >
//               <Box
//                 sx={{
//                   borderRadius: '50%',
//                   width: '80px',
//                   height: '80px',
//                   display: 'flex',
//                   alignItems: 'center',
//                   justifyContent: 'center',
//                   mb: 3,
//                 }}
//               >
//                 <SentimentSatisfied sx={{ color: '#4caf50', fontSize: '30px' }} />
//               </Box>
//               <Typography variant="h6" sx={{ color: '#4caf50', fontWeight: 'bold', mb: 1 }}>
//                 All Good! 🎉
//               </Typography>
//               <Typography variant="body2" sx={{ color: '#2e7d32' }}>
//                 No department leaves recorded
//               </Typography>
//             </Box>
//           ) : (
//             <Grid container spacing={2}>
//               {Object.entries(leavesByDepartment).map(([dept, leaves], index) => (
//                 <Grid item xs={12} md={6} key={index}
//                 sx={{width: isLargeScreen ? '49%' : '100%'}}>
//                   <DepartmentCard
//                     department={dept}
//                     absences={leaves}
//                     getDepartmentStats={getDepartmentStats}
//                     formatLeaveType={formatLeaveType}
//                     getLeaveTypeColor={getLeaveTypeColor}
//                   />
//                 </Grid>
//               ))}
//             </Grid>
//           )}
          
//         </Box> */}
//         {/* Latest Leaves */}
//         {/* <Box sx={{ mt: 4, width: isLargeScreen ? '33%' : '100%' }}> */}
//         {/* <Box sx={{ mt: 4, width:'33%'}}>
//           <LatestAbsences
//             latestAbsences={latestLeaves}
//             getLeaveTypeColor={getLeaveTypeColor}
//             formatLeaveType={formatLeaveType}
//             title="🕐 Latest Leave Requests"
//           />
//         </Box> */}
        
//       </Box>
//     </Box>
//   );
// };

// export default LeaveDashboard;